// ============================================================================
//  CrmPipelineConfig.tsx — Configuration du pipeline commercial
//  Créer / modifier / réordonner / supprimer les étapes
// ============================================================================

import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Edit2, Trash2, GripVertical, Check, X, AlertCircle,
  ArrowUp, ArrowDown, Settings, ChevronRight,
} from 'lucide-react'
import { crmApi, type PipelineStage } from '@/modules/crm/api/crm.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore }          from '@/stores/auth.store'

const Spinner = () => <div className="w-4 h-4 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />

const inputCls = 'w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]'

const COLORS = [
  'var(--brand-orange)','var(--brand-amber)','#10B981','#F59E0B','#EF4444',
  '#8B5CF6','#06B6D4','#F97316','#84CC16','#EC4899',
]

export function PipelineConfigPage() {
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)
  const { hasPermission } = useAuthStore()
  const canManage = hasPermission('crm:configure')

  const [editingId, setEditingId]   = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const { data: stages = [], isLoading } = useQuery({
    queryKey: ['crm-stages'],
    queryFn:  () => crmApi.getStages(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => crmApi.deleteStage(id),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['crm-stages'] }); addNotif({ id: Date.now().toString(), type: 'success', title: 'Étape désactivée' }) },
    onError:    (err: any) => addNotif({ id: Date.now().toString(), type: 'error', title: err?.response?.data?.message ?? 'Erreur' }),
  })

  const moveUp = (index: number) => {
    if (index === 0) return
    const ids = (stages as PipelineStage[]).map(s => s.id)
    ;[ids[index - 1], ids[index]] = [ids[index], ids[index - 1]]
    reorderMutation.mutate(ids)
  }

  const moveDown = (index: number) => {
    if (index === (stages as PipelineStage[]).length - 1) return
    const ids = (stages as PipelineStage[]).map(s => s.id)
    ;[ids[index], ids[index + 1]] = [ids[index + 1], ids[index]]
    reorderMutation.mutate(ids)
  }

  const reorderMutation = useMutation({
    mutationFn: (stageIds: string[]) => crmApi.reorderStages(stageIds),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['crm-stages'] }),
  })

  return (
    <div className="max-w-2xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--brand-dark)', letterSpacing: '-0.02em' }}>
            Configuration du pipeline
          </h1>
          <p style={{ fontSize: 13, color: 'var(--tx3)', marginTop: 2 }}>
            Créez et organisez les étapes de votre processus commercial
          </p>
        </div>
        {canManage && (
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
            style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
            <Plus className="w-4 h-4" /> Nouvelle étape
          </button>
        )}
      </div>

      {/* Légende */}
      <div className="p-4 bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl text-xs text-[var(--tx2)] space-y-1">
        <p>💡 <strong>Journal requis</strong> : bloque le déplacement si aucune activité récente</p>
        <p>✅ <strong>Étape Gagné</strong> : déclenche automatiquement la commission</p>
        <p>❌ <strong>Étape Perdu</strong> : archive l'opportunité</p>
      </div>

      {/* Liste des étapes */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <div className="space-y-2">
          {(stages as PipelineStage[]).map((stage, index) => (
            editingId === stage.id ? (
              <StageEditRow key={stage.id} stage={stage}
                onSaved={() => { queryClient.invalidateQueries({ queryKey: ['crm-stages'] }); setEditingId(null) }}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div key={stage.id}
                className="flex items-center gap-3 p-4 bg-white border border-[var(--bd)] rounded-xl group hover:border-[var(--blue)]/30 transition-all">
                {/* Ordre visuel */}
                <div className="flex flex-col gap-0.5 shrink-0">
                  {canManage && (
                    <>
                      <button onClick={() => moveUp(index)} disabled={index === 0}
                        className="p-0.5 text-[var(--tx3)] hover:text-[var(--brand-dark)] disabled:opacity-20">
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button onClick={() => moveDown(index)} disabled={index === (stages as PipelineStage[]).length - 1}
                        className="p-0.5 text-[var(--tx3)] hover:text-[var(--brand-dark)] disabled:opacity-20">
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>

                {/* Couleur + numéro */}
                <div className="flex items-center gap-2 shrink-0">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color ?? 'var(--tx3)' }} />
                  <span className="text-xs text-[var(--tx3)] font-mono w-4">{stage.order}</span>
                </div>

                {/* Info principale */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[var(--brand-dark)]">{stage.name}</p>
                    <span className="text-xs px-2 py-0.5 bg-[var(--surface-2)] text-[var(--tx3)] rounded-full">
                      {stage.probability}%
                    </span>
                    {stage.isWon  && <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">✅ Gagné</span>}
                    {stage.isLost && <span className="text-xs px-2 py-0.5 bg-red-50 text-red-700 rounded-full">❌ Perdu</span>}
                    {stage.isJournalRequired && <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full">📝 Journal requis</span>}
                  </div>
                  <p className="text-xs text-[var(--tx3)] mt-0.5">
                    {stage._count?.opportunities ?? 0} opportunité{(stage._count?.opportunities ?? 0) > 1 ? 's' : ''} active{(stage._count?.opportunities ?? 0) > 1 ? 's' : ''}
                  </p>
                </div>

                {/* Actions */}
                {canManage && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setEditingId(stage.id)}
                      className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[var(--blue)] hover:bg-[var(--surface-2)]"
                      title="Modifier">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => window.confirm(`Désactiver l'étape "${stage.name}" ?`) && deleteMutation.mutate(stage.id)}
                      className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-red-500 hover:bg-red-50"
                      title="Désactiver">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )
          ))}

          {(stages as PipelineStage[]).length === 0 && (
            <div className="flex flex-col items-center py-16 gap-4">
              <Settings className="w-12 h-12 text-[var(--tx3)]" />
              <div className="text-center">
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-dark)' }}>Aucune étape configurée</p>
                <p style={{ fontSize: 13, color: 'var(--tx3)', marginTop: 4 }}>Créez votre première étape pour commencer</p>
              </div>
              <button onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
                <Plus className="w-4 h-4" /> Créer une étape
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal création */}
      {showCreate && (
        <StageCreateModal
          nextOrder={(stages as PipelineStage[]).length + 1}
          onClose={() => setShowCreate(false)}
          onCreated={() => { queryClient.invalidateQueries({ queryKey: ['crm-stages'] }); addNotif({ id: Date.now().toString(), type: 'success', title: 'Étape créée' }); setShowCreate(false) }}
        />
      )}
    </div>
  )
}

// ── Ligne d'édition inline ────────────────────────────────────────────────────

function StageEditRow({ stage, onSaved, onCancel }: { stage: PipelineStage; onSaved: () => void; onCancel: () => void }) {
  const [form, setForm] = useState({
    name:              stage.name,
    probability:       stage.probability.toString(),
    color:             stage.color ?? 'var(--brand-orange)',
    isJournalRequired: stage.isJournalRequired,
    isWon:             stage.isWon,
    isLost:            stage.isLost,
  })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const toggle = (k: string) => setForm(p => ({ ...p, [k]: !(p as any)[k] }))

  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.updateStage(stage.id, {
      name:              form.name,
      probability:       Number(form.probability),
      color:             form.color,
      isJournalRequired: form.isJournalRequired,
      isWon:             form.isWon,
      isLost:            form.isLost,
    }),
    onSuccess: onSaved,
  })

  return (
    <div className="p-4 bg-[var(--surface-2)] border-2 border-[var(--blue)]/40 rounded-xl space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="block text-xs text-[var(--tx3)] mb-1">Nom *</label>
          <input className={inputCls} value={form.name} onChange={f('name')} autoFocus />
        </div>
        <div>
          <label className="block text-xs text-[var(--tx3)] mb-1">Probabilité %</label>
          <input className={inputCls} type="number" min="0" max="100" value={form.probability} onChange={f('probability')} />
        </div>
      </div>

      {/* Couleur */}
      <div>
        <label className="block text-xs text-[var(--tx3)] mb-2">Couleur</label>
        <div className="flex gap-2 flex-wrap">
          {COLORS.map(c => (
            <button key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
              className={`w-7 h-7 rounded-full border-2 transition-all ${form.color === c ? 'border-[var(--brand-dark)] scale-110' : 'border-transparent'}`}
              style={{ backgroundColor: c }} />
          ))}
          <input type="color" value={form.color} onChange={f('color')}
            className="w-7 h-7 rounded-full border border-[var(--bd)] cursor-pointer" title="Couleur personnalisée" />
        </div>
      </div>

      {/* Flags */}
      <div className="flex gap-4 flex-wrap">
        {[
          { key: 'isJournalRequired', label: '📝 Journal obligatoire' },
          { key: 'isWon',  label: '✅ Étape Gagné' },
          { key: 'isLost', label: '❌ Étape Perdu' },
        ].map(opt => (
          <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={(form as any)[opt.key]} onChange={() => toggle(opt.key)}
              className="rounded border-[var(--bd)] text-[var(--blue)]" />
            <span className="text-xs text-[var(--tx2)]">{opt.label}</span>
          </label>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !form.name.trim()}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
          style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
          {isPending ? <Spinner /> : <Check className="w-3.5 h-3.5" />} Sauvegarder
        </button>
      </div>
    </div>
  )
}

// ── Modal création ────────────────────────────────────────────────────────────

function StageCreateModal({ nextOrder, onClose, onCreated }: { nextOrder: number; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    name: '', probability: '0', color: COLORS[nextOrder % COLORS.length],
    isJournalRequired: true, isWon: false, isLost: false,
  })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))
  const toggle = (k: string) => setForm(p => ({ ...p, [k]: !(p as any)[k] }))

  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.createStage({
      name:              form.name,
      order:             nextOrder,
      probability:       Number(form.probability),
      color:             form.color,
      isJournalRequired: form.isJournalRequired,
      isWon:             form.isWon,
      isLost:            form.isLost,
    }),
    onSuccess: onCreated,
  })

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--brand-dark)]">Nouvelle étape</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)]" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <label className="block text-xs text-[var(--tx3)] mb-1">Nom *</label>
              <input className={inputCls} value={form.name} onChange={f('name')} autoFocus placeholder="Ex: Qualification" />
            </div>
            <div>
              <label className="block text-xs text-[var(--tx3)] mb-1">Probabilité %</label>
              <input className={inputCls} type="number" min="0" max="100" value={form.probability} onChange={f('probability')} />
            </div>
          </div>

          <div>
            <label className="block text-xs text-[var(--tx3)] mb-2">Couleur</label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(c => (
                <button key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${form.color === c ? 'border-[var(--brand-dark)] scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            {[
              { key: 'isJournalRequired', label: '📝 Journal d\'activité obligatoire pour passer à cette étape' },
              { key: 'isWon',  label: '✅ Cette étape représente un deal gagné (déclenche la commission)' },
              { key: 'isLost', label: '❌ Cette étape représente un deal perdu' },
            ].map(opt => (
              <label key={opt.key} className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={(form as any)[opt.key]} onChange={() => toggle(opt.key)}
                  className="mt-0.5 rounded border-[var(--bd)] text-[var(--blue)]" />
                <span className="text-xs text-[var(--tx2)]">{opt.label}</span>
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
            <button onClick={() => mutate()} disabled={isPending || !form.name.trim()}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
              {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PipelineConfigPage
